/**
 * Thin client for the dev-only HTTP control endpoints (M2.7 server).
 *
 * These mint local WebSocket tickets and seat players by name — the dev stand-in
 * for auth + join (replaced by the real control plane in M5/M7). Everything here
 * is behind the `/dev/` prefix and clearly labeled dev-only.
 */

import type { Role } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import type { HostTicket, PlayerTicket } from './api-types';

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(`${SERVER_HTTP_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Resolve a join code → gameId. The dev server has a single game, so any code maps
 * to it; the dev `/dev/join` endpoint ignores the id anyway. Returns a placeholder
 * so the shared call site works identically in dev and prod.
 */
export async function resolveGameByCode(code: string): Promise<string> {
  void code; // ignored: the dev server has one game
  return 'dev-game';
}

/**
 * Mint a host or presentation ticket (no identity needed for these in dev). The dev
 * server hosts exactly one game, so a resolved `gameId` is meaningless here; it's
 * accepted for a uniform signature with the prod client (#51) and ignored.
 */
export async function requestSpectatorTicket(
  role: Extract<Role, 'host' | 'presentation'>,
  gameId?: string,
): Promise<HostTicket> {
  void gameId;
  const res = await postJson('/dev/ticket', { role });
  if (!res.ok) throw new Error(`Failed to get ${role} ticket (${res.status})`);
  return (await res.json()) as HostTicket;
}

/**
 * Seat a player by name + mint their ticket. The dev server has no real identity,
 * so it accepts a remembered `playerId` directly (FR-2.6, dev-grade) — the `identity`
 * shape matches the prod client for a uniform call site (a `guestToken`, if present,
 * is ignored by the dev endpoint). Throws with the server's message on rejection.
 */
export async function joinAsPlayer(
  name: string,
  identity?: { guestToken?: string; playerId?: string },
  // The dev server hosts exactly one game, so a join code / gameId is meaningless
  // here; accepted for a uniform signature with the prod client and ignored.
  gameId?: string,
  joinCode?: string,
): Promise<PlayerTicket> {
  void gameId;
  void joinCode;
  const res = await postJson('/dev/join', {
    name,
    ...(identity?.playerId ? { playerId: identity.playerId } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Join failed (${res.status})`);
  }
  return (await res.json()) as PlayerTicket;
}
